import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Globe, Signal, Wifi, Map, Shield, Zap } from 'lucide-react';

const GlobalSignalPage = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <div className="container mx-auto px-4 py-20">
        {/* Back Button */}
        <Link
          to="/self-phone"
          className="inline-flex items-center text-indigo-400 hover:text-indigo-300 transition-colors mb-12"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to SELF Phone
        </Link>

        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400">
            Global Satellite Communication Without Limits
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-6">
            Experience true communication freedom with global satellite coverage and no carrier dependencies. The SELF Phone's Global Signal system combines advanced satellite technology, decentralized mesh networks, and autonomous routing to ensure you stay connected anywhere on Earth. This isn't just communication – it's complete digital sovereignty, disaster resilience, and borderless connectivity all in one.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-indigo-950/30 p-8 rounded-2xl backdrop-blur-sm border border-indigo-500/20"
          >
            <Globe className="w-12 h-12 text-indigo-400 mb-4" />
            <h3 className="text-xl font-semibold mb-4">Direct Satellite Links</h3>
            <p className="text-gray-300">
              Powered by expanding partnerships with leading satellite providers including Starlink, ensuring reliable global coverage with multiple redundant paths.
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-indigo-950/30 p-8 rounded-2xl backdrop-blur-sm border border-indigo-500/20"
          >
            <Signal className="w-12 h-12 text-indigo-400 mb-4" />
            <h3 className="text-xl font-semibold mb-4">Carrier Independence</h3>
            <p className="text-gray-300">
              No traditional carrier plans needed. Communicate freely anywhere on Earth with built-in satellite connectivity and mesh networking capabilities.
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-indigo-950/30 p-8 rounded-2xl backdrop-blur-sm border border-indigo-500/20"
          >
            <Map className="w-12 h-12 text-indigo-400 mb-4" />
            <h3 className="text-xl font-semibold mb-4">Global Coverage</h3>
            <p className="text-gray-300">
              Future-proof signal access even in remote regions. Stay connected wherever your journey takes you, from urban centers to the most remote locations.
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-indigo-950/30 p-8 rounded-2xl backdrop-blur-sm border border-indigo-500/20"
          >
            <Wifi className="w-12 h-12 text-indigo-400 mb-4" />
            <h3 className="text-xl font-semibold mb-4">Signal Mesh Access</h3>
            <p className="text-gray-300">
              Full access to the Signal Mesh network, enabling seamless communication and data transfer across the globe through decentralized nodes.
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-indigo-950/30 p-8 rounded-2xl backdrop-blur-sm border border-indigo-500/20"
          >
            <Shield className="w-12 h-12 text-indigo-400 mb-4" />
            <h3 className="text-xl font-semibold mb-4">Disaster Resilience</h3>
            <p className="text-gray-300">
              Stay connected during natural disasters or infrastructure outages with autonomous signal routing and redundant communication paths.
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-indigo-950/30 p-8 rounded-2xl backdrop-blur-sm border border-indigo-500/20"
          >
            <Zap className="w-12 h-12 text-indigo-400 mb-4" />
            <h3 className="text-xl font-semibold mb-4">Instant Activation</h3>
            <p className="text-gray-300">
              No complex setup or registration required. Your SELF Phone automatically connects to available satellite and mesh networks for immediate global access.
            </p>
          </motion.div>
        </div>

        {/* Phone Number Information */}
        <div className="max-w-4xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-blue-500/20 p-8 rounded-2xl backdrop-blur-sm border border-indigo-500/20"
          >
            <h3 className="text-2xl font-semibold mb-4 text-center">Phone Number Information</h3>
            <div className="space-y-4 text-gray-300">
              <p className="text-center mb-6">
                Every SELF Phone comes with a new, dedicated phone number for your global communications.
              </p>
              <div className="bg-indigo-950/30 p-6 rounded-xl border border-indigo-500/10">
                <p className="mb-4">
                  <span className="text-indigo-400 font-semibold">Current Status:</span> We provide new phone numbers for all users. Unfortunately, we are not currently able to support porting existing numbers to the SELF Phone.
                </p>
                <p>
                  <span className="text-indigo-400 font-semibold">Future Plans:</span> We are actively working on a solution to support number porting in the future. Stay tuned for updates as we expand our capabilities.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Real-World Applications */}
        <div className="max-w-4xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-indigo-950/30 p-8 rounded-2xl backdrop-blur-sm border border-indigo-500/20"
          >
            <h3 className="text-2xl font-semibold mb-6 text-center">Real-World Applications</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start">
                  <span className="w-2 h-2 bg-indigo-400 rounded-full mr-3 mt-2"></span>
                  <div>
                    <h4 className="font-semibold mb-1">Disaster Response Teams</h4>
                    <p className="text-gray-300">Maintain critical communications during natural disasters when traditional infrastructure fails.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="w-2 h-2 bg-indigo-400 rounded-full mr-3 mt-2"></span>
                  <div>
                    <h4 className="font-semibold mb-1">Remote Workers</h4>
                    <p className="text-gray-300">Stay connected while working from anywhere, including remote locations and during travel.</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start">
                  <span className="w-2 h-2 bg-indigo-400 rounded-full mr-3 mt-2"></span>
                  <div>
                    <h4 className="font-semibold mb-1">Off-Grid Communities</h4>
                    <p className="text-gray-300">Enable reliable communication for communities beyond traditional infrastructure reach.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="w-2 h-2 bg-indigo-400 rounded-full mr-3 mt-2"></span>
                  <div>
                    <h4 className="font-semibold mb-1">Global Enterprises</h4>
                    <p className="text-gray-300">Coordinate operations across borders without relying on multiple carrier contracts.</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Coming Enhancements */}
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-indigo-950/30 p-8 rounded-2xl backdrop-blur-sm border border-indigo-500/20"
          >
            <h3 className="text-2xl font-semibold mb-6 text-center">Coming Enhancements</h3>
            <div className="space-y-6">
              <div className="flex items-start">
                <span className="w-2 h-2 bg-indigo-400 rounded-full mr-3 mt-2"></span>
                <div>
                  <h4 className="font-semibold mb-1">Expanded LEO Network Integration</h4>
                  <p className="text-gray-300">New partnerships with emerging satellite providers to increase coverage density and reduce latency globally.</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="w-2 h-2 bg-indigo-400 rounded-full mr-3 mt-2"></span>
                <div>
                  <h4 className="font-semibold mb-1">Enhanced Mesh Protocol</h4>
                  <p className="text-gray-300">Advanced peer-to-peer networking capabilities enabling users to create and expand local mesh networks automatically.</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="w-2 h-2 bg-indigo-400 rounded-full mr-3 mt-2"></span>
                <div>
                  <h4 className="font-semibold mb-1">Intelligent Signal Routing</h4>
                  <p className="text-gray-300">AI-powered routing algorithms that optimize connection paths in real-time based on network conditions and user needs.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default GlobalSignalPage; 